import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, Star, Eye, EyeOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const field =
  "w-full px-4 py-3 rounded-xl bg-black/40 border border-amber-500/20 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-400/60";
const empty = { title: "", tag: "", description: "", price: "", duration: "", featured: false, visible: true };

export const ServicesManager = () => {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get("/admin/services").then(({ data }) => setList(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (s) => { setForm({ ...empty, ...s }); setEditId(s.id); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { title: form.title, tag: form.tag, description: form.description, price: form.price, duration: form.duration, featured: form.featured, visible: form.visible };
      if (editId) await api.put(`/admin/services/${editId}`, payload);
      else await api.post("/admin/services", payload);
      toast.success(editId ? "Service mis à jour" : "Service ajouté");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce service ?")) return;
    await api.delete(`/admin/services/${id}`);
    toast.success("Service supprimé");
    load();
  };

  const toggleVisible = async (s) => {
    await api.put(`/admin/services/${s.id}`, { ...s, visible: !s.visible });
    load();
  };

  return (
    <div data-testid="admin-services-manager">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="font-serif text-3xl text-amber-50">Services &amp; Formations</h1>
          <p className="text-slate-400">Gérez vos offres visibles sur le site.</p>
        </div>
        <button onClick={openNew} data-testid="admin-add-service-button" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-amber-300 to-yellow-600 text-[#08080A] font-medium hover:scale-[1.02] transition-transform">
          <Plus size={18} /> Ajouter
        </button>
      </div>

      <div className="space-y-4">
        {list.map((s) => (
          <div key={s.id} className="glass rounded-2xl p-6 flex items-start justify-between gap-4" data-testid={`service-row-${s.id}`}>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                {s.featured && <Star size={14} className="text-amber-400 fill-amber-400" />}
                <h3 className="font-serif text-xl text-amber-100">{s.title}</h3>
                {s.tag && <span className="text-xs font-mono-accent text-amber-400/70">{s.tag}</span>}
                {!s.visible && <span className="text-xs px-2 py-0.5 rounded-full bg-slate-700/50 text-slate-400">Masqué</span>}
              </div>
              <p className="text-slate-400 text-sm">{s.description}</p>
              <div className="flex gap-4 mt-2 text-xs text-amber-200/60 font-mono-accent">
                {s.duration && <span>{s.duration}</span>}
                {s.price && <span>{s.price}</span>}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => toggleVisible(s)} data-testid={`service-toggle-${s.id}`} className="p-2 rounded-lg hover:bg-white/5 text-slate-400" title="Visibilité">
                {s.visible ? <Eye size={16} /> : <EyeOff size={16} />}
              </button>
              <button onClick={() => openEdit(s)} data-testid={`service-edit-${s.id}`} className="p-2 rounded-lg hover:bg-white/5 text-amber-300"><Pencil size={16} /></button>
              <button onClick={() => remove(s.id)} data-testid={`service-delete-${s.id}`} className="p-2 rounded-lg hover:bg-red-500/10 text-red-400"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0D0D12] border-amber-500/20 text-slate-100 max-w-lg">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-amber-100">{editId ? "Modifier le service" : "Nouveau service"}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <input className={field} placeholder="Titre" data-testid="service-form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <input className={field} placeholder="Étiquette (ex: Parcours phare)" value={form.tag} onChange={(e) => setForm({ ...form, tag: e.target.value })} />
            <textarea rows={3} className={field} placeholder="Description" data-testid="service-form-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <div className="grid grid-cols-2 gap-3">
              <input className={field} placeholder="Durée" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} />
              <input className={field} placeholder="Tarif" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
            </div>
            <div className="flex gap-6 pt-1">
              <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="accent-amber-500" /> Mis en avant</label>
              <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={form.visible} onChange={(e) => setForm({ ...form, visible: e.target.checked })} className="accent-amber-500" /> Visible</label>
            </div>
          </div>
          <DialogFooter>
            <button onClick={save} disabled={saving || !form.title} data-testid="service-form-save" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-amber-300 to-yellow-600 text-[#08080A] font-medium hover:scale-[1.02] transition-transform disabled:opacity-60">
              {saving ? <Loader2 size={18} className="animate-spin" /> : "Enregistrer"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
